/**
 * Adding something, in as few taps as the situation allows.
 *
 * There is no "where" field any more, and that is the point of this screen rather than an
 * omission from it. Where a food goes is a property of the food, answered once on the
 * Groceries screen by somebody with time to think about it, so at the moment of standing in
 * the kitchen holding a bag there is nothing left to decide — tap the thing, it lands where it
 * lives.
 *
 * What is left is a list and a search over it: the household's own recent things first,
 * because a household converges fast on the same forty, and everything else it keeps behind
 * the same field. A name that is on no list at all gets one, silently, with every answer
 * guessed from the catalog and every guess correctable afterwards.
 *
 * This takes the whole screen rather than sitting over part of it. Half-typed entries are the
 * cheapest thing in the app to lose and the most annoying: the tab strip sat a thumb's width
 * from the field, and one stray tap on Shopping threw the entry away silently. Covering the
 * tabs means the only way out is the one that admits it is a way out.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';

import type { Actions } from '../actions';
import type { Grocery, InventoryItem, Location } from '../api/types';
import { resolveGrocery } from '../domain/groceries';
import { isOutOfStock } from '../domain/levels';
import { placeName } from './groceries';
import { LookingUp } from './waiting';

interface Props {
  groceries: Grocery[];
  items: InventoryItem[];
  locations: Location[];
  actions: Actions;
  onAdded: () => void;
  onClose: () => void;
}

/** Enough to fill a thumb's reach without becoming a list to read. */
const RECENT_SHOWN = 8;

export function QuickAdd({ groceries, items, locations, actions, onAdded, onClose }: Props) {
  const [wanted, setWanted] = useState('');
  const [alreadyHere, setAlreadyHere] = useState<InventoryItem | null>(null);
  // What the last tap did, phrased for a person. Kept until the next one rather than timed out:
  // it is a statement about what happened, not an offer to reverse it, and the line it sits on
  // is reserved either way.
  const [landed, setLanded] = useState<string | null>(null);
  // The name currently being looked up, or nothing. A name rather than a flag, so the screen
  // can say what it is waiting on even after the field has moved on.
  const [lookingUp, setLookingUp] = useState<string | null>(null);
  const waiting = lookingUp !== null;

  // A modal that covers everything has to answer the gesture a keyboard reaches for first.
  useEffect(() => {
    const dismiss = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', dismiss);
    return () => window.removeEventListener('keydown', dismiss);
  }, [onClose]);

  const offered = useMemo(
    () => offerings(groceries, items, wanted),
    [groceries, items, wanted],
  );
  const unlisted = wanted.trim() !== '' && resolveGrocery(groceries, wanted) === null;

  /**
   * The receipt for a tap, and the only place this screen ever says where anything went.
   *
   * Where a food lives is never asked here, which is the point of the screen — so the moment
   * after the tap is the one moment the user finds out. Without it the list simply reorders
   * under the thumb, which reads as a tap that missed rather than one that worked.
   */
  const noteArrival = (item: InventoryItem): void => {
    setLanded(`${item.name} — added to ${placeName(locations, item.location_id)}`);
  };

  /** Restocking says so rather than saying where: the line was already in the kitchen. */
  const refill = async (item: InventoryItem): Promise<void> => {
    const restocked = await actions.restock(item);
    setLanded(`${restocked.name} — restocked`);
    setAlreadyHere(null);
    setWanted('');
    onAdded();
  };

  const add = async (grocery: Grocery): Promise<void> => {
    const here = await actions.alreadyHere(grocery);

    // A line the household has run out of is not a question. "Restock or add another" is worth
    // asking only when there is still some of the old one, because that is the case where two
    // dates would be two different things; an empty line has nothing to keep apart.
    if (here !== null && isOutOfStock(here)) {
      await refill(here);
      return;
    }

    if (here !== null) {
      setAlreadyHere(here);
      return;
    }

    noteArrival(await actions.addItem(grocery));
    setWanted('');
    onAdded();
  };

  /**
   * A name nobody has listed gets an entry first, guessed rather than asked about.
   *
   * The only slow thing this screen does, because a food the catalog has never heard of is
   * asked about over the network. Nothing else here waits on anything, so the wait is said out
   * loud and the controls that started it are shut for the duration — a second tap would be a
   * second paid call and a second of everything the first one was about to add.
   */
  const addUnlisted = async (): Promise<void> => {
    const asked = wanted.trim();
    if (waiting || asked === '') {
      return;
    }

    setLookingUp(asked);
    try {
      const listed = await actions.listGrocery(asked);
      if (listed !== null) {
        await add(listed);
      }
    } finally {
      setLookingUp(null);
    }
  };

  return (
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Add">
      <header>
        <h2>Add</h2>
        <button type="button" class="close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </header>

      <div class="sheet-body">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (waiting) {
              return;
            }
            const first = offered[0];
            void (first === undefined ? addUnlisted() : add(first));
          }}
        >
          <input
            type="search"
            value={wanted}
            placeholder="What did you get?"
            autofocus
            enterkeyhint="done"
            onInput={(event) => {
              setWanted((event.target as HTMLInputElement).value);
              // The panel answers one tap. Typing is the next thing being looked for, so
              // leaving it up would make it a notice with no way past it but adding.
              setAlreadyHere(null);
            }}
          />
        </form>

        <JustAdded said={landed} />

        {alreadyHere === null ? null : (
          <AlreadyHere
            item={alreadyHere}
            place={placeName(locations, alreadyHere.location_id)}
            onRestock={() => void refill(alreadyHere)}
          />
        )}

        {offered.length === 0 && !unlisted ? (
          <p class="empty">
            Nothing on your list yet. Type what you bought and it will be added to it.
          </p>
        ) : (
          <>
            {/* Eight rows with nothing over them read as the whole list. What they actually are,
                until something is typed, is what the household bought most recently — and a
                heading is the cheapest way to say so. Typing replaces it with a search, which
                the field itself already labels. */}
            {wanted.trim() === '' ? <h3 class="offered-head">Recently added</h3> : null}
            <ul class="suggestions">
              {offered.map((grocery) => (
                <li key={grocery.slug}>
                  <button type="button" disabled={waiting} onClick={() => void add(grocery)}>
                    {grocery.name}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* The wait replaces the control that started it rather than sitting beside it: the
            thing the thumb is still resting on is the thing that has to change. */}
        {lookingUp !== null ? (
          <LookingUp name={lookingUp} />
        ) : unlisted ? (
          <button type="button" class="primary" onClick={() => void addUnlisted()}>
            Add “{wanted.trim()}” to your groceries
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What the last tap did, said out loud.
 *
 * Drawn whether or not there is anything to say, for two reasons that happen to want the same
 * thing. A live region has to be on the page before the text arrives or a screen reader will
 * not announce it. And an empty line still holds its height, so the list underneath never jumps
 * a row's worth under a thumb that is about to tap again — which on this screen would add the
 * wrong food entirely.
 */
export function JustAdded({ said }: { said: string | null }) {
  return (
    <p class="landed" role="status">
      {said}
    </p>
  );
}

/**
 * What to show for what has been typed so far.
 *
 * An empty field offers what the household has bought most recently, because opening this with
 * nothing to tap would waste the one screen where speed matters most. Anything typed searches
 * the whole list, in the order the list is kept.
 */
export function offerings(
  groceries: Grocery[],
  items: InventoryItem[],
  wanted: string,
): Grocery[] {
  const query = wanted.trim().toLowerCase();

  if (query !== '') {
    return groceries.filter(
      (grocery) => grocery.name.toLowerCase().includes(query) || grocery.slug.includes(query),
    );
  }

  const lastUsed = new Map<string, string>();
  for (const item of items) {
    const seen = lastUsed.get(item.food_ref ?? '');
    if (item.food_ref !== null && (seen === undefined || seen < item.added_at)) {
      lastUsed.set(item.food_ref, item.added_at);
    }
  }

  return [...groceries]
    .sort((left, right) =>
      (lastUsed.get(left.slug) ?? '') < (lastUsed.get(right.slug) ?? '') ? 1 : -1,
    )
    .slice(0, RECENT_SHOWN);
}

/**
 * What this sheet says when the household already has the thing being added.
 *
 * It offers one answer, because there is only one worth offering: buying more of something
 * you have is more of that thing. "Add another" sat beside it and was the wrong reading
 * nearly every time — and the row it minted was indistinguishable from the first afterwards,
 * which is the one kind of mess this app has no gesture for clearing up.
 *
 * What is left is worth the tap for what it *says* rather than for the choice it offers. This
 * is the screen that never asks where anything goes, so being told the food is already in the
 * fridge is news, and it arrives at the only moment somebody can act on it.
 */
export function AlreadyHere({
  item,
  place,
  onRestock,
}: {
  item: InventoryItem;
  place: string;
  onRestock: () => void;
}) {
  return (
    <div class="already">
      <p class="note">
        You already have {item.name} in the {place}.
      </p>
      <button type="button" class="primary" onClick={onRestock}>
        Restock it
      </button>
    </div>
  );
}
